import {
  Badge,
  Box,
  Chip,
  LinearProgress,
  Stack,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowModesModel,
} from "@mui/x-data-grid";
import moment from "jalali-moment";
import GridViewMUIX from "src/components/GridViewMUIX";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "src/context/auth";
import { useDashboardData } from "src/context/dashboard-data";
import { useEffect, useState, useCallback, useMemo, memo } from "react";
import { useNotifications } from "@toolpad/core/useNotifications";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import SendIcon from "@mui/icons-material/Send";
import SmsIcon from "@mui/icons-material/Sms";
import { IFollowupSchedule } from "src/interface/IFollowupSchedule";
import {
  ListOfStatusGlobal,
  StatusGlobal,
} from "src/interface/enum/StatusGlobal";
import {
  BootstrapTooltip,
  handleCopy,
  handleSendMessage,
  processFollowupData,
  getGroupedServiceRefs,
} from "src/utilities";
import NewFollowupSchedule from "src/pages/followups-schedule/new-followup-schedule";
import { ISystem } from "src/interface/ISystem";
import LanguageIcon from "@mui/icons-material/Language";
import PasswordIcon from "@mui/icons-material/Password";
import KeyIcon from "@mui/icons-material/Key";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import { IAppUser } from "src/interface/IUser";
import ChangeStatus from "src/components/status-log/ChangeStatus";
import { IBankAccount } from "src/interface/IBankAccount";
import BankAccountForm from "./bank-account-form";
import { getRoutes } from "src/utilities/routes";
import CustomAccordion from "src/components/AccordinBarComponent";
import NewUser from "src/pages/users/new-user";
import BulkUpdateCreatorDialog from "src/components/BulkUpdateCreatorDialog";
import LoadingButton from "@mui/lab/LoadingButton";
import SmsHistoryPage from "src/pages/sms/sms-history";
import { getUnreadInboundSmsCountByPhone } from "src/api/sms-history";

// ==================== تنظیمات اولیه ====================

interface FollowupsScheduleDashboardProps {
  followupType: "Agreement" | "Customer";
  error?: any;
  rows: IFollowupSchedule[];
  isLoading: boolean;
  refetch?: any;
}

// کامپوننت جداگانه برای دکمه پیامک با Badge
const SmsButtonWithBadge = memo(
  ({
    phoneNumber,
    customerId,
    token,
    onOpenDialog,
  }: {
    phoneNumber?: string;
    customerId?: string;
    token?: string;
    onOpenDialog: (phone: string, customerRef: string) => void;
  }) => {
    const { data: unreadCount = 0 } = useQuery({
      queryKey: ["unread-sms-count", phoneNumber],
      queryFn: async () => {
        if (!phoneNumber || !token) return 0;
        return await getUnreadInboundSmsCountByPhone(token, phoneNumber);
      },
      enabled: !!phoneNumber && !!token,
      staleTime: 30 * 1000, // 30 ثانیه
      refetchInterval: 60 * 1000, // هر 60 ثانیه یکبار
    });

    if (!phoneNumber) return null;

    const smsIcon = (
      <Badge
        badgeContent={unreadCount > 0 ? unreadCount : undefined}
        color="error"
      >
        <SmsIcon color="primary" />
      </Badge>
    );

    return (
      <BootstrapTooltip
        title={unreadCount > 0 ? `پیامک (${unreadCount} خوانده نشده)` : "پیامک"}
      >
        <GridActionsCellItem
          icon={smsIcon}
          label="پیامک"
          color="inherit"
          onClick={() => {
            onOpenDialog(phoneNumber, customerId || "");
          }}
        />
      </BootstrapTooltip>
    );
  },
);

const FollowupsScheduleDashboard: React.FC<FollowupsScheduleDashboardProps> =
  memo(({ followupType, error, rows, refetch, isLoading }) => {
    const notifications = useNotifications();
    const { user } = useAuth();
    const [openActionForm, setOpenActionForm] = useState<boolean>(false);
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const [followupId, setFollowupId] = useState<number>();
    const [openEditUser, setOpenEditUser] = useState<boolean>(false);
    const [selectedUserId, setSelectedUserId] = useState<string>();
    const [openBulkUpdateDialog, setOpenBulkUpdateDialog] =
      useState<boolean>(false);
    const [selectedFollowups, setSelectedFollowups] = useState<
      IFollowupSchedule[]
    >([]);
    const [selectedRowIds, setSelectedRowIds] = useState<any[]>([]);
    const [openBulkSmsDialog, setOpenBulkSmsDialog] = useState(false);
    const [bulkSmsText, setBulkSmsText] = useState("");
    const [openSmsHistoryDialog, setOpenSmsHistoryDialog] = useState(false);
    const [selectedCustomerPhone, setSelectedCustomerPhone] =
      useState<string>("");
    const [selectedCustomerRef, setSelectedCustomerRef] = useState<string>("");
    const [paginationModels, setPaginationModels] = useState<
      Record<string, { page: number; pageSize: number }>
    >({});
    const Routes = getRoutes();

    // ==================== مدیریت خطاها ====================
    useEffect(() => {
      if (error) {
        handleSendMessage(
          notifications,
          "خطا در دریافت اطلاعات" + error.toString(),
          "error",
        );
        console.log(error);
      }
    }, [error, notifications]);

    // ==================== درخواست‌های API ====================
    const { mutateAsync: GetSystemById, isPending: isPendingGetSystemById } =
      useMutation({
        mutationFn: async (systemId: string) => {
          const response = await fetch(`${Routes.SystemsRoutes}${systemId}`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${user?.token}`,
              Accept: "application/json",
            },
          });
          return await response.json();
        },
        onSuccess: (responseData) => {
          JSON.stringify(responseData);
        },
        onError: (error) => {
          handleSendMessage(
            notifications,
            "خطا در دریافت اطلاعات" + error,
            "error",
          );
          console.log(error);
        },
      });

    // ==================== استفاده از Context ====================
    const {
      serviceGroups,
      services,
      isLoadingServiceGroups,
      isLoadingServices,
    } = useDashboardData();

    const { mutateAsync: getUserById, isPending: isPendingUserById } =
      useMutation({
        mutationFn: async (userId: string) => {
          const response = await fetch(`${Routes.UsersRoutes}${userId}`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${user?.token}`,
              Accept: "application/json",
            },
          });
          return await response.json();
        },
        onSuccess: (responseData) => {
          JSON.stringify(responseData);
        },
        onError: (error) => {
          handleSendMessage(
            notifications,
            "خطا در دریافت اطلاعات" + error,
            "error",
          );
          console.log(error);
        },
      });

    const { mutateAsync: bulkSendSms, isPending: isPendingBulkSms } =
      useMutation({
        mutationFn: async (body: {
          followupIds: number[];
          message: string;
        }) => {
          const response = await fetch(
            `${Routes.FollowupScheduleSmsRoutes}/bulk-send`,
            {
              method: "POST",
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${user?.token}`,
              },
              body: JSON.stringify(body),
            },
          );
          return await response.json();
        },
        onSuccess: (responseData) => {
          JSON.stringify(responseData);
        },
        onError: (error) => {
          handleSendMessage(
            notifications,
            "خطا در ارسال پیامک گروهی " + error,
            "error",
          );
          console.log(error);
        },
      });

    const handleOpenSystem = async (systemRef: string) => {
      const system: ISystem = await GetSystemById(systemRef);
      if (system && system.url) {
        window.open(system.url, "_blank");
      } else {
        handleSendMessage(notifications, "خطا در باز کردن لینک سیستم", "error");
      }
    };

    const [openChangeTask, setOpenChangeTask] = useState(false);
    const [rowInfo, setRowInfo] = useState<IFollowupSchedule>();
    const handleEditStatusClick = useCallback(
      (row: IFollowupSchedule) => () => {
        setOpenChangeTask(true);
        setRowInfo(row);
      },
      [],
    );

    const handleOpenSmsDialog = useCallback(
      (phone: string, customerRef: string) => {
        setSelectedCustomerPhone(phone);
        setSelectedCustomerRef(customerRef);
        setOpenSmsHistoryDialog(true);
      },
      [],
    );

    const columns: GridColDef[] = useMemo(
      () => [
        {
          field: "actions",
          headerAlign: "center",
          headerName: "عملیات",
          align: "center",
          sortable: false,
          disableColumnMenu: true,
          cellClassName: "actions center-vertical",
          minWidth: 130,
          renderCell: (params) => {
            return (
              <Stack direction="column" alignItems="center">
                <Stack direction="row">
                  <BootstrapTooltip title="تغییر وضعیت">
                    <GridActionsCellItem
                      icon={<SyncProblemIcon color="warning" />}
                      label="تغییر وضعیت"
                      className="textPrimary"
                      onClick={handleEditStatusClick(params.row)}
                      color="inherit"
                    />
                  </BootstrapTooltip>
                  <BootstrapTooltip title="مدیریت پیگیری‌ها">
                    <GridActionsCellItem
                      icon={<ManageHistoryIcon color="primary" />}
                      label="مدیریت پیگیری‌ها"
                      className="textPrimary"
                      onClick={() => {
                        setFollowupId(params.id as number);
                        setOpenActionForm(true);
                      }}
                      color="inherit"
                    />
                  </BootstrapTooltip>
                  <BootstrapTooltip title="سامانه">
                    <GridActionsCellItem
                      icon={<LanguageIcon color="secondary" />}
                      label="سامانه"
                      sx={{
                        display: params.row.appUserWebSystem ? "block" : "none",
                      }}
                      onClick={() =>
                        handleOpenSystem(params.row.appUserWebSystem.systemRef)
                      }
                      color="inherit"
                    />
                  </BootstrapTooltip>
                  <SmsButtonWithBadge
                    phoneNumber={params.row.customer?.phoneNumber || undefined}
                    customerId={params.row.customer?.id || undefined}
                    token={user?.token || undefined}
                    onOpenDialog={handleOpenSmsDialog}
                  />
                </Stack>

                <Stack direction="row">
                  <BootstrapTooltip title="کپی نام کاربری">
                    <GridActionsCellItem
                      icon={<KeyIcon color="primary" />}
                      label="کپی نام کاربری"
                      sx={{
                        display: params.row.appUserWebSystem ? "block" : "none",
                      }}
                      onClick={(event) =>
                        handleCopy(
                          params.row.appUserWebSystem.userName,
                          notifications,
                        )
                      }
                      color="inherit"
                    />
                  </BootstrapTooltip>
                  <BootstrapTooltip title="کپی پسورد">
                    <GridActionsCellItem
                      icon={<PasswordIcon color="primary" />}
                      label="کپی پسورد"
                      sx={{
                        display: params.row.appUserWebSystem ? "block" : "none",
                      }}
                      onClick={() =>
                        handleCopy(
                          params.row.appUserWebSystem.password,
                          notifications,
                        )
                      }
                      color="inherit"
                    />
                  </BootstrapTooltip>
                  <BootstrapTooltip title="کارت‌های بانکی">
                    <GridActionsCellItem
                      sx={{
                        display:
                          followupType === "Agreement" ? "block" : "none",
                      }}
                      icon={<CreditCardIcon color="success" />}
                      label="کارت‌های بانکی"
                      color="inherit"
                      onClick={() => handleCardsBankClick(params.row)}
                    />
                  </BootstrapTooltip>

                  <BootstrapTooltip title="ویرایش کاربر">
                    <GridActionsCellItem
                      icon={<PersonIcon color="info" />}
                      label="ویرایش کاربر"
                      color="inherit"
                      onClick={() => {
                        setSelectedUserId(params.row.customer?.id);
                        setOpenEditUser(true);
                      }}
                    />
                  </BootstrapTooltip>
                </Stack>
              </Stack>
            );
          },
        },
        {
          field: "desc",
          headerName: "توضیحات",
          align: "center",
          headerAlign: "center",
          editable: false,
          sortable: true,
          minWidth: 150,
          cellClassName: "center-vertical",
          // valueGetter: (value: any, row: any) => {
          //   // ترکیب توضیحات اصلی و توضیحات فرزند برای جستجو؛ فقط وقتی هر دو مقدار دارند
          //   const mainDesc = value || "";
          //   console.log("🚀 ~ mainDesc:", mainDesc);
          //   const childDesc = row.childDesc || "";
          //   if (mainDesc && childDesc)
          //     return `${mainDesc} *** ${childDesc}`.trim();
          //   return "";
          // },
          renderCell: (params: GridRenderCellParams<any, string>) => {
            return (
              <Typography style={{ whiteSpace: "pre-line" }}>
                {params.value + "\n" + params.row.childDesc}
              </Typography>
            );
          },
        },
        {
          field: "priroty",
          headerName: "اولویت",
          align: "center",
          headerAlign: "center",
          editable: false,
          sortable: true,
          width: 50,
          cellClassName: "center-vertical",
        },
        {
          field: "title",
          headerName: "عنوان",
          align: "left",
          headerAlign: "left",
          editable: false,
          sortable: true,
          minWidth: 150,
          cellClassName: "center-vertical",
          renderCell: (params: GridRenderCellParams<any, string>) => {
            return (
              <Typography style={{ whiteSpace: "pre-line" }}>
                {params.value}
                {"\n"}
                {params.row.followUpGroup?.title ?? ""}
              </Typography>
            );
          },
        },
        {
          field: "customerName",
          headerName: "نام مشتری",
          align: "left",
          headerAlign: "left",
          editable: false,
          sortable: true,
          minWidth: 150,
          cellClassName: "center-vertical",
          renderCell: (params: GridRenderCellParams<any, string>) => {
            return (
              <Typography style={{ whiteSpace: "pre-line" }}>
                {params.value}
              </Typography>
            );
          },
        },

        {
          field: "mobile",
          headerName: "موبایل",
          align: "left",
          headerAlign: "left",
          editable: false,
          sortable: true,
          minWidth: 100,
          cellClassName: "center-vertical",
          valueGetter: (value: any, row: any) => {
            // استفاده از phoneNumber مشتری برای جستجو
            return row.customer?.phoneNumber || "";
          },
          renderCell: (params: GridRenderCellParams<any, string>) => {
            return (
              <Typography style={{ whiteSpace: "pre-line" }}>
                {params.row.customer?.phoneNumber}
              </Typography>
            );
          },
        },
        {
          field: "lastTaskLog",
          headerName: "وضعیت",
          align: "center",
          headerAlign: "center",
          editable: false,
          type: "singleSelect",
          valueOptions: ListOfStatusGlobal,
          sortable: true,
          cellClassName: "center-vertical",
          width: 110,
          valueGetter: (value: any, row: any) => {
            if (!row || !row.lastTaskLog) return StatusGlobal.NotStarted;
            return row.lastTaskLog.statusTask || StatusGlobal.NotStarted;
          },
          renderCell: (params) => {
            const status = ListOfStatusGlobal.find(
              (g) => g.value === params.value,
            );
            return (
              <Chip
                label={status?.label}
                color={status?.color as any}
                variant="filled"
              />
            );
          },
        },
        {
          field: "dueOn",
          headerName: "تاریخ سر رسید",
          align: "center",
          headerAlign: "center",
          editable: false,
          sortable: true,
          width: 110,
          cellClassName: "center-vertical",
          renderCell: (params: GridRenderCellParams<any, string>) => {
            if (!params.value) return "-";

            const dueDate = moment(params.value, "YYYY-MM-DD");
            const today = moment().startOf("day");
            const diffDays = dueDate.diff(today, "days");

            return (
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body2">
                  {moment
                    .from(params.value, "en", "YYYY-MM-DD")
                    .format("jYYYY/jMM/jDD")}
                </Typography>
                {diffDays > 0 ? (
                  <Chip
                    label={`${diffDays} روزمانده`}
                    color="success"
                    variant="filled"
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                ) : diffDays < 0 ? (
                  <Chip
                    label={`${Math.abs(diffDays)} روز گذشته`}
                    color="error"
                    variant="filled"
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                ) : (
                  <Chip
                    label="امروز سررسید"
                    color="warning"
                    variant="filled"
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Box>
            );
          },
        },
        {
          field: "creatorRef",
          headerName: "کارمند",
          align: "center",
          headerAlign: "center",
          editable: false,
          sortable: true,
          minWidth: 50,
          cellClassName: "center-vertical",
          valueGetter: (value: any, row: any) => {
            if (!row || !row.creator) return "";
            return (row.creator.fName || "") + " " + (row.creator.lName || "");
          },
          renderCell: (params) => {
            const creator = params.row.creator;
            if (!creator) return null;

            const value = (creator.fName || "") + " " + (creator.lName || "");
            const initials = value
              .trim()
              .split(" ")
              .filter(Boolean)
              .map((word) => word.charAt(0).toUpperCase())
              .join(" ");

            return (
              <BootstrapTooltip title={value}>
                <p>{initials}</p>
              </BootstrapTooltip>
            );
          },
        },
      ],
      [followupType, handleEditStatusClick],
    );

    const [bankAccInfo, setBankAccInfo] = useState<IBankAccount[]>([]);
    const [bankAccShow, setBankAccShow] = useState<boolean>(false);

    const handleCardsBankClick = async (row: IFollowupSchedule) => {
      const user: IAppUser = await getUserById(
        row.agreement?.customerRef ?? "0",
      );
      if (user) {
        setBankAccShow(true);
        setBankAccInfo(user.bankAccountsUser ?? []);
      }
    };

    const newActionClose = () => {
      setOpenActionForm(false);
    };

    const handleTaskChangeClose = () => {
      setOpenChangeTask(false);
    };

    const handleEditUserClose = () => {
      setOpenEditUser(false);
      setSelectedUserId(undefined);
    };

    const handleBulkUpdateClose = () => {
      setOpenBulkUpdateDialog(false);
      setSelectedFollowups([]);
    };

    const handleBulkUpdateSuccess = () => {
      refetch?.();
    };

    const handleBulkUpdateClick = (followups: IFollowupSchedule[]) => {
      setSelectedFollowups(followups);
      setOpenBulkUpdateDialog(true);
    };

    const handleBulkSmsOpen = () => {
      if (selectedRowIds.length === 0) {
        handleSendMessage(
          notifications,
          "حداقل یک پیگیری را انتخاب کنید",
          "warning",
        );
        return;
      }
      setOpenBulkSmsDialog(true);
    };

    const handleBulkSmsSend = async () => {
      if (!bulkSmsText.trim()) {
        handleSendMessage(notifications, "متن پیامک خالی است", "warning");
        return;
      }
      const payload = {
        followupIds: selectedRowIds as number[],
        message: bulkSmsText.trim(),
      };
      const res = await bulkSendSms(payload);
      if (res?.status === true) {
        handleSendMessage(
          notifications,
          `ارسال پیامک: موفق ${res.data?.sent ?? 0} / ناموفق ${res.data?.failed ?? 0}`,
          "success",
        );
        setOpenBulkSmsDialog(false);
        setBulkSmsText("");
      } else {
        handleSendMessage(
          notifications,
          res?.message || "ارسال پیامک ناموفق بود",
          "error",
        );
      }
    };

    const handleSelectionChange = (selectedIds: readonly any[]) => {
      setSelectedRowIds([...selectedIds]);
      const selectedFollowups = processedFollowupData.filter((row) =>
        selectedIds.includes(row.id),
      );
      setSelectedFollowups(selectedFollowups);
    };

    const handlePaginationModelChange =
      (gridUniqueName: string) =>
      (model: { page: number; pageSize: number }) => {
        setPaginationModels((prev) => ({
          ...prev,
          [gridUniqueName]: model,
        }));
      };

    // ==================== پردازش داده‌های پیگیری ====================
    const filteredFollowupData = useMemo(() => {
      if (!rows) return [];

      return followupType === "Agreement"
        ? rows.filter((r: IFollowupSchedule) => r.agreementRef !== null)
        : rows.filter((r: IFollowupSchedule) => r.agreementRef === null);
    }, [rows, followupType]);

    // پردازش داده‌ها با اطلاعات سرویس و نام مشتری
    const processedFollowupData = useMemo(() => {
      if (!services) return filteredFollowupData;
      const processed = processFollowupData(filteredFollowupData, services);

      // اضافه کردن فیلدهای نام مشتری و کارمند برای جستجو
      return processed.map((row) => ({
        ...row,
        customerName: row.customer
          ? `${row.customer.fName} ${row.customer.lName}${row.customer.coName ? `\n${row.customer.coName}` : ""}`
          : "",
        creatorName: row.creator
          ? `${row.creator.fName} ${row.creator.lName}`
          : "",
      }));
    }, [filteredFollowupData, services]);

    // دریافت serviceRef های گروه‌بندی شده
    const groupedServiceRefs = useMemo(() => {
      if (!serviceGroups || !services) return new Set();
      return getGroupedServiceRefs(serviceGroups, services);
    }, [serviceGroups, services]);

    // گروه‌بندی پیگیری‌ها بر اساس گروه‌های سرویس
    const groupedFollowups = useMemo(() => {
      if (!serviceGroups || !services) return [];

      return serviceGroups
        .map((group) => {
          const groupServices = services.filter(
            (s) => s.serviceGroupId === group.id,
          );
          const checkService = processedFollowupData.filter((f) =>
            groupServices.some((s) => s.id === f.serviceRef),
          );
          return { group, followups: checkService };
        })
        .filter((item) => item.followups.length > 0);
    }, [serviceGroups, services, processedFollowupData]);

    // دریافت کارهای شخصی
    const personalTasks = useMemo(() => {
      return processedFollowupData.filter(
        (f) => !groupedServiceRefs.has(f.serviceRef),
      );
    }, [processedFollowupData, groupedServiceRefs]);

    const [expanded, setExpanded] = useState<string | false>(
      "panel-personal-tasks",
    );

    const handleChange =
      (panel: string) =>
      (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
      };

    // Custom toolbar component for bulk operations
    const BulkUpdateToolbar = useCallback(() => {
      return (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {selectedFollowups.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<GroupIcon />}
              onClick={() => handleBulkUpdateClick(selectedFollowups)}
              size="small"
            >
              تغییر کارمند ({selectedFollowups.length})
            </Button>
          )}
          {selectedRowIds.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={handleBulkSmsOpen}
              size="small"
            >
              ارسال پیامک ({selectedRowIds.length})
            </Button>
          )}
        </Box>
      );
    }, [selectedFollowups, handleBulkUpdateClick, selectedRowIds.length]);

    return (
      <>
        <NewFollowupSchedule
          open={openActionForm}
          handleClose={newActionClose}
          refetch={refetch}
          isEdit={!!followupId}
          followupId={followupId}
          setFollowupId={setFollowupId}
        />
        <ChangeStatus
          open={openChangeTask}
          handleClose={handleTaskChangeClose}
          refetch={refetch}
          FollowupData={rowInfo}
        />
        <BankAccountForm
          rows={bankAccInfo}
          handleClose={() => setBankAccShow(false)}
          open={bankAccShow}
        />
        <NewUser
          open={openEditUser}
          setOpenBank={handleEditUserClose}
          refetch={refetch}
          isEdit={true}
          userRef={selectedUserId}
          userRole="Customer"
          setUserId={setSelectedUserId}
          usersData={[]}
          tags={[]}
        />
        <BulkUpdateCreatorDialog
          open={openBulkUpdateDialog}
          onClose={handleBulkUpdateClose}
          selectedFollowups={selectedFollowups}
          onSuccess={handleBulkUpdateSuccess}
        />
        <Dialog
          open={openBulkSmsDialog}
          onClose={() => setOpenBulkSmsDialog(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>ارسال پیامک گروهی</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1 }}>
              تعداد انتخاب‌شده: {selectedRowIds.length}
            </Typography>
            <TextField
              label="متن پیامک"
              fullWidth
              multiline
              minRows={4}
              value={bulkSmsText}
              onChange={(e) => setBulkSmsText(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenBulkSmsDialog(false)}>انصراف</Button>
            <LoadingButton
              loading={isPendingBulkSms}
              variant="contained"
              onClick={handleBulkSmsSend}
            >
              ارسال
            </LoadingButton>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openSmsHistoryDialog}
          onClose={() => setOpenSmsHistoryDialog(false)}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              height: "90vh",
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle>تاریخچه پیامک</DialogTitle>
          <DialogContent dividers sx={{ p: 0, height: "100%" }}>
            <SmsHistoryPage
              defaultPhone={selectedCustomerPhone}
              defaultCustomerRef={selectedCustomerRef}
              hideFilters={true}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenSmsHistoryDialog(false)}>بستن</Button>
          </DialogActions>
        </Dialog>
        <Box
          sx={{
            width: {
              xs: 370,
              sm: 500,
              md: 720,
              lg: "calc(100% - 80px)",
              xl: "calc(100% - 80px)",
            },
          }}
        >
          {(isLoading ||
            isPendingGetSystemById ||
            isPendingUserById ||
            isLoadingServiceGroups ||
            isLoadingServices) && <LinearProgress />}

          {groupedFollowups.map(({ group, followups }) => (
            <CustomAccordion
              panelKey={`panel-group-${group.id}`}
              key={group.id}
              title={
                <Badge badgeContent={followups.length} color="primary">
                  {group.name}
                </Badge>
              }
              expanded={expanded}
              onChange={handleChange}
            >
              <GridViewMUIX
                columns={columns}
                loading={isLoading}
                pageSize={10}
                rowModesModel={rowModesModel}
                rowHeight={60}
                sortFields={{ field: "modifiedOn", sort: "desc" }}
                gridUniqueName={`FollowupsScheduleDashboard-${followupType}-${group.id}`}
                rows={followups}
                setRowModesModel={setRowModesModel}
                EditToolbar={BulkUpdateToolbar}
                onSelectionChange={handleSelectionChange}
                paginationModel={
                  paginationModels[
                    `FollowupsScheduleDashboard-${followupType}-${group.id}`
                  ] || { page: 0, pageSize: 10 }
                }
                onPaginationModelChange={handlePaginationModelChange(
                  `FollowupsScheduleDashboard-${followupType}-${group.id}`,
                )}
              />
            </CustomAccordion>
          ))}

          {personalTasks.length > 0 && (
            <CustomAccordion
              panelKey="panel-personal-tasks"
              key="personal-tasks"
              title={
                <Badge badgeContent={personalTasks.length} color="secondary">
                  کارهای شخصی
                </Badge>
              }
              expanded={expanded}
              onChange={handleChange}
            >
              <GridViewMUIX
                columns={columns}
                loading={isLoading}
                pageSize={10}
                rowModesModel={rowModesModel}
                rowHeight={60}
                sortFields={{ field: "modifiedOn", sort: "desc" }}
                gridUniqueName={`FollowupsScheduleDashboard-${followupType}-personal-tasks`}
                rows={personalTasks}
                setRowModesModel={setRowModesModel}
                EditToolbar={BulkUpdateToolbar}
                onSelectionChange={handleSelectionChange}
                paginationModel={
                  paginationModels[
                    `FollowupsScheduleDashboard-${followupType}-personal-tasks`
                  ] || { page: 0, pageSize: 10 }
                }
                onPaginationModelChange={handlePaginationModelChange(
                  `FollowupsScheduleDashboard-${followupType}-personal-tasks`,
                )}
              />
            </CustomAccordion>
          )}
        </Box>
      </>
    );
  });

export default FollowupsScheduleDashboard;
